import { Link, useNavigate } from "react-router-dom";
import { ChevronRight, Lock, MapPin, Building2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const STYLE = {
  section: (on) => `shrink-0 h-9 px-3 rounded-full text-sm border flex items-center gap-1.5 transition-colors ${on ? "bg-stone-900 text-white border-stone-900" : "bg-white border-[#E6E1D7] hover:border-stone-400"}`,
  view: (on) => `shrink-0 h-8 px-3 rounded-full text-xs border flex items-center gap-1.5 transition-colors ${on ? "bg-[#F3EFE6] border-stone-400 font-semibold text-stone-900" : "bg-white border-[#E6E1D7] text-stone-600 hover:text-stone-900"}`,
};

function Switcher({ level, label, items, current, testid, compact }) {
  const nav = useNavigate();
  return (
    <>
      <nav aria-label={label} className={`${compact ? "hidden md:flex" : "flex"} gap-1.5 overflow-x-auto no-scrollbar -mx-1 px-1 py-0.5`} data-testid={testid}>
        {items.map((i) => (
          <Link key={i.id} to={i.to} aria-current={i.id === current ? "page" : undefined} data-testid={`${testid}-${i.id}`} className={`${STYLE[level](i.id === current)} focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-stone-900/30`}>
            {i.label}{i.locked && <Lock className="h-3 w-3 text-stone-400" aria-label="Requires a plan upgrade" />}
          </Link>
        ))}
      </nav>
      {compact && (
        <label className="md:hidden flex items-center gap-2 rounded-xl border border-[#E6E1D7] bg-white pl-3 pr-2 h-11" data-testid={`${testid}-picker`}>
          <span className="text-[11px] uppercase tracking-wider text-stone-500 shrink-0">{level === "section" ? "Section" : "View"}</span>
          <select aria-label={label} value={current} onChange={(e) => nav(items.find((i) => i.id === e.target.value).to)} data-testid={`${testid}-select`}
            className="flex-1 min-w-0 bg-transparent text-sm font-semibold outline-none">
            {items.map((i) => <option key={i.id} value={i.id}>{i.label}</option>)}
          </select>
        </label>
      )}
    </>
  );
}

export default function WorkspaceBar({ ws }) {
  const { locations, locationId } = useAuth();
  if (!ws) return null;
  const { section: s, child: c, view: v } = ws;
  const group = v.scope === "group";
  const loc = locations.find((l) => l.id === locationId)?.name;
  return (
    <div className="no-print mb-5 space-y-2" data-testid="workspace-bar">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
        <nav aria-label="Breadcrumb" className="flex items-center gap-1 text-[11px] uppercase tracking-[0.16em] font-semibold text-stone-500 min-w-0" data-testid="workspace-breadcrumb">
          <Link to={s.to} className="hover:text-stone-900 truncate">{s.label}</Link>
          {c.label !== s.label && <><ChevronRight className="h-3 w-3 shrink-0" /><span className="text-stone-900 truncate" data-testid="workspace-current">{c.label}</span></>}
        </nav>
        <span data-testid="workspace-scope" className={`sm:ml-auto inline-flex items-center gap-1 rounded-full px-2.5 h-6 text-[11px] font-medium ${group ? "bg-stone-900 text-white" : "bg-[#F3EFE6] text-stone-700"}`}>
          {group ? <><Building2 className="h-3 w-3" />All locations · group view</> : <><MapPin className="h-3 w-3" />{loc || "Active location"}</>}
        </span>
      </div>
      {s.children.length > 1 && <Switcher level="section" label={`${s.label} sections`} items={s.children} current={c.id} testid="ws-sections" compact={s.children.length > 3} />}
      {c.views.length > 1 && <Switcher level="view" label={`${c.label} views`} items={c.views} current={v.id} testid="ws-views" compact={c.views.length > 4} />}
    </div>
  );
}
